

import { saveAs } from 'file-saver';

const XLSX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const XML_ALLOWED_CONTROL_CHARACTERS = new Set([0x09, 0x0a, 0x0d]);
const XML_DISALLOWED_NONCHARACTERS = new Set([0xfffe, 0xffff]);

const escapeXml = (value) => {
  const sanitizedValue = [...`${value ?? ''}`]
    .filter((character) => {
      const codePoint = character.codePointAt(0);
      return (
        XML_ALLOWED_CONTROL_CHARACTERS.has(codePoint) ||
        (codePoint >= 0x20 && !XML_DISALLOWED_NONCHARACTERS.has(codePoint))
      );
    })
    .join('');

  return sanitizedValue
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
};

const buildCell = (value) => {
  return `<c t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`;
};

const normalizeXlsxFileName = (fileName) => {
  const baseFileName = `${fileName || 'export.xlsx'}`.replace(/\.xls$/i, '');

  return baseFileName.toLowerCase().endsWith('.xlsx')
    ? baseFileName
    : `${baseFileName}.xlsx`;
};

const normalizeWorksheetName = (worksheetName) => {
  const truncatedName = `${worksheetName || 'Sheet1'}`
    .replace(/[[:*?/\\\]]/g, ' ')
    .trim()
    .slice(0, 31);
  let startIndex = 0;
  let endIndex = truncatedName.length;

  while (truncatedName[startIndex] === "'") {
    startIndex += 1;
  }
  while (endIndex > startIndex && truncatedName[endIndex - 1] === "'") {
    endIndex -= 1;
  }

  const normalizedName = truncatedName.slice(startIndex, endIndex).trim();

  return normalizedName || 'Sheet1';
};

const crcTable = Array.from({ length: 256 }, (_, index) => {
  let crc = index;

  for (let bit = 0; bit < 8; bit += 1) {
    crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
  }

  return crc >>> 0;
});

const getCrc32 = (bytes) => {
  let crc = 0xffffffff;

  bytes.forEach((byte) => {
    crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });

  return (crc ^ 0xffffffff) >>> 0;
};

const writeUint16 = (bytes, value) => {
  bytes.push(value & 0xff, (value >>> 8) & 0xff);
};

const writeUint32 = (bytes, value) => {
  bytes.push(
    value & 0xff,
    (value >>> 8) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 24) & 0xff,
  );
};

const appendBytes = (target, source) => {
  for (const byte of source) {
    target.push(byte);
  }
};

const buildZip = (files) => {
  const encoder = new TextEncoder();
  const zipBytes = [];
  const centralDirectory = [];

  files.forEach(({ name, content }) => {
    const nameBytes = encoder.encode(name);
    const contentBytes = encoder.encode(content);
    const crc = getCrc32(contentBytes);
    const localHeaderOffset = zipBytes.length;

    writeUint32(zipBytes, 0x04034b50);
    writeUint16(zipBytes, 20);
    writeUint16(zipBytes, 0);
    writeUint16(zipBytes, 0);
    writeUint16(zipBytes, 0);
    writeUint16(zipBytes, 0);
    writeUint32(zipBytes, crc);
    writeUint32(zipBytes, contentBytes.length);
    writeUint32(zipBytes, contentBytes.length);
    writeUint16(zipBytes, nameBytes.length);
    writeUint16(zipBytes, 0);
    appendBytes(zipBytes, nameBytes);
    appendBytes(zipBytes, contentBytes);

    centralDirectory.push({
      nameBytes,
      crc,
      size: contentBytes.length,
      localHeaderOffset,
    });
  });

  const centralDirectoryOffset = zipBytes.length;

  centralDirectory.forEach(({ nameBytes, crc, size, localHeaderOffset }) => {
    writeUint32(zipBytes, 0x02014b50);
    writeUint16(zipBytes, 20);
    writeUint16(zipBytes, 20);
    writeUint16(zipBytes, 0);
    writeUint16(zipBytes, 0);
    writeUint16(zipBytes, 0);
    writeUint16(zipBytes, 0);
    writeUint32(zipBytes, crc);
    writeUint32(zipBytes, size);
    writeUint32(zipBytes, size);
    writeUint16(zipBytes, nameBytes.length);
    writeUint16(zipBytes, 0);
    writeUint16(zipBytes, 0);
    writeUint16(zipBytes, 0);
    writeUint16(zipBytes, 0);
    writeUint32(zipBytes, 0);
    writeUint32(zipBytes, localHeaderOffset);
    appendBytes(zipBytes, nameBytes);
  });

  const centralDirectorySize = zipBytes.length - centralDirectoryOffset;

  writeUint32(zipBytes, 0x06054b50);
  writeUint16(zipBytes, 0);
  writeUint16(zipBytes, 0);
  writeUint16(zipBytes, centralDirectory.length);
  writeUint16(zipBytes, centralDirectory.length);
  writeUint32(zipBytes, centralDirectorySize);
  writeUint32(zipBytes, centralDirectoryOffset);
  writeUint16(zipBytes, 0);

  return new Uint8Array(zipBytes);
};

const buildWorkbook = ({ headers, rows, worksheetName }) => {
  const headerRow = `<row>${headers.map((header) => buildCell(header)).join('')}</row>`;
  const dataRows = rows
    .map((row) => `<row>${row.map((cell) => buildCell(cell)).join('')}</row>`)
    .join('');
  const sheetName = escapeXml(normalizeWorksheetName(worksheetName));

  return buildZip([
    {
      name: '[Content_Types].xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`,
    },
    {
      name: '_rels/.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
    },
    {
      name: 'xl/workbook.xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="${sheetName}" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`,
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
</Relationships>`,
    },
    {
      name: 'xl/worksheets/sheet1.xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetData>
    ${headerRow}
    ${dataRows}
  </sheetData>
</worksheet>`,
    },
  ]);
};

export const exportRowsAsXls = ({ headers = [], rows = [], fileName = 'export.xls', worksheetName = 'Sheet1' }) => {
  const normalizedFileName = normalizeXlsxFileName(fileName);
  const workbook = buildWorkbook({ headers, rows, worksheetName });

  const blob = new Blob([workbook], {
    type: XLSX_MIME_TYPE,
  });

  saveAs(blob, normalizedFileName);
};
